
import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Factory } from "@/hooks/useFactories";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";

interface FactoryCheckboxSelectProps {
  factories: Factory[];
  selectedFactoryNames: string[];
  onChange: (factoryNames: string[]) => void;
  isLoading: boolean;
}

export function FactoryCheckboxSelect({
  factories,
  selectedFactoryNames,
  onChange,
  isLoading
}: FactoryCheckboxSelectProps) {
  const toggleFactory = (factoryName: string) => {
    const updatedSelection = selectedFactoryNames.includes(factoryName)
      ? selectedFactoryNames.filter(name => name !== factoryName)
      : [...selectedFactoryNames, factoryName];
    
    onChange(updatedSelection);
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading factories...</div>;
  }

  if (factories.length === 0) {
    return <div className="text-sm text-muted-foreground">No factories available</div>;
  }

  return (
    <div className="w-full">
      <div className="border rounded-md p-1">
        <ScrollArea className="h-[150px]">
          <div className="p-3 space-y-2">
            {factories.map(factory => (
              <div key={factory.id} className="flex items-center space-x-2">
                <Checkbox 
                  id={`factory-${factory.id}`} 
                  checked={selectedFactoryNames.includes(factory.name)}
                  onCheckedChange={() => toggleFactory(factory.name)} 
                />
                <label
                  htmlFor={`factory-${factory.id}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {factory.name}
                </label>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {selectedFactoryNames.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedFactoryNames.map(name => (
            <Badge 
              key={name} 
              variant="secondary"
              className="flex items-center gap-1"
            >
              {name}
              <button 
                className="ml-1 rounded-full outline-none focus:ring-2"
                onClick={() => toggleFactory(name)}
              >
                <span className="sr-only">Remove</span>
                <Check className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
